import React, { useState } from "react";
import { api } from "@/lib/api";
import { tl } from "@/lib/i18nFormat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { addPending, genClientId, PendingCapture } from "@/lib/connectivity";

import { useI18n } from "@/context/I18nContext";

export interface FieldDef {
  name: string;
  label: string;
  type?: "text" | "textarea" | "number" | "select" | "list";
  options?: { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  fields: FieldDef[];
  endpoint: string;
  onDone: () => void;
  testId?: string;
  offlineCapture?: { storeDayId: string; onQueued: () => void };
}

export const EventDialog: React.FC<Props> = ({ open, onOpenChange, title, fields, endpoint, onDone, testId, offlineCapture }) => {
  const {
    t
  } = useI18n();

  const [values, setValues] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState(false);

  const set = (k: string, v: any) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    for (const f of fields) {
      if (f.required && !values[f.name]) {
        toast.error(tl("{label} is required", { label: f.label }));
        return;
      }
    }
    const payload: Record<string, any> = {};
    for (const f of fields) {
      let v = values[f.name];
      if (v === undefined || v === "") continue;
      if (f.type === "number") v = parseFloat(v);
      if (f.type === "list") v = String(v).split(",").map((x) => x.trim()).filter(Boolean);
      payload[f.name] = v;
    }
    if (offlineCapture) payload.client_id = genClientId();
    setBusy(true);
    try {
      if (offlineCapture && !navigator.onLine) throw { offline: true };
      await api.post(endpoint, payload);
      toast.success(tl("{title} saved", { title }));
      setValues({});
      onOpenChange(false);
      onDone();
    } catch (err: any) {
      // Offline or network failure on a critical capture -> preserve locally, sync later (idempotent)
      if (offlineCapture && (err?.offline || !err?.response)) {
        const p: PendingCapture = {
          client_id: payload.client_id, endpoint, payload,
          storeDayId: offlineCapture.storeDayId,
          label: payload.note || payload.category || title,
          createdAt: new Date().toISOString(),
        };
        addPending(p);
        toast.message("Saved offline — will sync when reconnected");
        setValues({});
        onOpenChange(false);
        offlineCapture.onQueued();
      } else {
        toast.error(err?.response?.data?.detail || "Save failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    (<Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testId}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.name}>
              <Label>{f.label}{f.required && " *"}</Label>
              {f.type === "textarea" ? (
                <Textarea className="mt-1" value={values[f.name] || ""} placeholder={f.placeholder}
                  onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
              ) : f.type === "select" ? (
                <Select value={values[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger className="mt-1" data-testid={`field-${f.name}`}><SelectValue placeholder={t("Select")} /></SelectTrigger>
                  <SelectContent>{f.options?.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input className="mt-1" type={f.type === "number" ? t("number") : t("text")} value={values[f.name] || ""}
                  placeholder={f.placeholder || (f.type === "list" ? "comma,separated" : "")}
                  onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="event-dialog-submit">{busy ? t("Saving…") : t("Save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};
